import React, {Component} from 'react';

import {autobind} from 'core-decorators';
import Link from 'next/link';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Checkbox from 'Frontend/components/form/Checkbox';
import Input from 'Frontend/components/form/Input';
import Features from 'Frontend/components/quickInterview/Features';

import {FONTS} from 'Frontend/utils/fonts';
import {clampCss, pxToVw} from 'Frontend/utils/helpers';
import {MEDIA} from 'Frontend/utils/mediaQueries';

/**
 * Form
 *
 * @property {*} prop Prop comment.
 *
 * @class Form
 * @extends {Component}
 */
class Form extends Component {
    static propTypes = {
        className: PropTypes.string,
        data: PropTypes.object,
        errors: PropTypes.object,
        onChange: PropTypes.func
    }

    static defaultProps = {
        className: '',
        data: {},
        errors: {},
        onChange() {}
    }

    /**
     * Creates an instance of Form.
     *
     * @param {Object} props Component props.
     * @memberof Form
     */
    constructor(props) {
        super(props);

        this.handleFirstName = this.handleInput.bind(this, 'firstname');
        this.handleLastName = this.handleInput.bind(this, 'lastname');
        this.handleEmail = this.handleInput.bind(this, 'email');
        this.handlePhone = this.handleInput.bind(this, 'phone');
    }

    /**
     * Handles the Input changes.
     *
     * @param {String} key   The key to change.
     * @param {String} value The value of the input.
     * @memberof Form
     */
    @autobind
    handleInput(key, value) {
        const {onChange} = this.props;

        onChange(key, value);
    }


    /**
     * Handles the checkbox value.
     *
     * @param {Boolean} value If the checkbox is checked or not.
     * @memberof Form
     */
    @autobind
    handleCheckbox(value) {
        const {onChange} = this.props;

        onChange('optin', value);
    }

    /**
     * Renders the Component.
     *
     * @returns {JSX} Component.
     * @memberof Form
     */
    render() {
        const {className, data, errors} = this.props;
        const {
            email,
            firstname,
            lastname,
            optin,
            phone
        } = data;

        return (
            <FormWrapper className={className}>
                <Input
                    autocomplete="given-name"
                    error={errors.firstname}
                    label="Vorname"
                    type="text"
                    value={firstname}
                    onChange={this.handleFirstName}
                />
                <Input
                    autocomplete="family-name"
                    error={errors.lastname}
                    label="Nachname"
                    type="text"
                    value={lastname}
                    onChange={this.handleLastName}
                />
                <Input
                    autocomplete="email"
                    error={errors.email}
                    label="E-Mail"
                    type="email"
                    value={email}
                    onChange={this.handleEmail}
                />
                <Input
                    autocomplete="tel"
                    error={errors.phone}
                    info="Notwendig um deine Identität per SMS zu verifizieren"
                    label="Mobilfunknummer"
                    type="tel"
                    value={phone}
                    onChange={this.handlePhone}
                />
                <Features />
                <CheckboxContainer>
                    <StyledCheckbox
                        error={errors.optin}
                        label=""
                        value={optin}
                        onChange={this.handleCheckbox}
                    />
                    <Text>
                        {'Ich habe die geltenden '}
                        <Link href="/agb" passHref>
                            <StyledLink target="_blank">
                                AGB
                            </StyledLink>
                        </Link>
                        {' und '}
                        <Link href="/datenschutz" passHref>
                            <StyledLink target="_blank">
                                Datenschutzbestimmungen
                            </StyledLink>
                        </Link>
                        {' zur Kenntnis genommen.'}
                    </Text>
                </CheckboxContainer>
            </FormWrapper>
        );
    }
}

export default Form;

const StyledCheckbox = styled(Checkbox)`
    margin-bottom: 0;
    margin-left: ${clampCss(-12, 'm', true)};
    margin-right: 0;
    margin-top: 0;

    ${MEDIA.TABLET.UP} {
        margin-left: ${clampCss(-12, 't', true)};
    }

    ${MEDIA.DESKTOP.UP} {
        margin-left: min(-12px, ${pxToVw(-12, 'd')});
    }

    & > div:last-child {
        padding: ${clampCss(12, 'm')};
    }
`;

const CheckboxContainer = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;

    margin: 0 0 ${clampCss(24, 'm')} 0;

    ${MEDIA.TABLET.UP} {
        margin: 0 0 ${clampCss(24, 't')} 0;
    }

    ${MEDIA.DESKTOP.UP} {
        margin: 0 0 max(24px, ${pxToVw(24, 'd')}) 0;
    }
`;

const Text = styled.p`
    ${FONTS.REGULAR}
    color: ${({theme}) => theme.colors.textColor};
    font-size: ${clampCss(12, 'm')};
    letter-spacing: ${clampCss(0.5, 'm')};
    line-height: ${clampCss(16.8, 'm')};
    margin: 0px;

    ${MEDIA.TABLET.UP} {
        font-size: ${clampCss(12, 't')};
        letter-spacing: ${clampCss(0.5, 't')};
        line-height: ${clampCss(16.8, 't')};
    }

    ${MEDIA.DESKTOP.UP} {
        font-size: max(12px, ${pxToVw(12, 'd')});
        letter-spacing: max(0.5px, ${pxToVw(0.5, 'd')});
        line-height: max(16.8px, ${pxToVw(16.8, 'd')});
    }
`;


const FormWrapper = styled.div`
    flex: 1 1 auto;
    width: 100%;
`;

const StyledLink = styled.a`
    ${FONTS.SEMIBOLD}
    color: #D56B50;
    text-decoration: none;
`;